import { t } from '@web/i18n';
import { Breadcrumb } from '@web/ui/core/molecules';
import { AppBar } from '@web/ui/core/organisms';

export interface OverviewTopBarProps {
  /** `null` while the snapshot is loading — renders a placeholder. */
  aiCount: number | null;
  humanCount: number | null;
  /** The snapshot request failed: counts show a dash instead of pulsing. */
  failed?: boolean;
}

function Count({
  value,
  failed,
}: Readonly<{ value: number | null; failed: boolean }>): React.JSX.Element {
  if (failed) {
    return (
      <span data-testid="overview-topbar-count-unavailable" className="text-faint-foreground">
        –
      </span>
    );
  }
  if (value === null) {
    return (
      <span
        aria-hidden
        data-testid="overview-topbar-count-loading"
        className="inline-block h-3 w-5 animate-pulse rounded-sm bg-muted"
      />
    );
  }
  return <span className="tabular-nums font-medium text-foreground">{value}</span>;
}

/**
 * Overview page top bar — leading breadcrumb on the left, live AI /
 * human indicators on the right. Uses the shared `AppBar` shell so
 * the height, divider and slot rhythm stay identical to every other
 * screen.
 *
 * Counts are accepted as props so the data source can change without
 * touching the component.
 */
export function OverviewTopBar({
  aiCount,
  humanCount,
  failed = false,
}: Readonly<OverviewTopBarProps>): React.JSX.Element {
  return (
    <AppBar
      leading={<Breadcrumb items={[{ icon: 'house', label: t('overview.crumb.current') }]} />}
      trailing={
        <div className="inline-flex items-center gap-3 font-mono text-tiny uppercase tracking-eyebrow text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="h-dot-sm w-dot-sm rounded-pill bg-tone-green-fg flex-shrink-0"
            />
            <Count value={aiCount} failed={failed} />
            <span>{t('overview.topBar.ai')}</span>
          </span>
          <span aria-hidden className="text-faint-foreground">
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Count value={humanCount} failed={failed} />
            <span>{t('overview.topBar.human')}</span>
          </span>
        </div>
      }
    />
  );
}
